import React, { useEffect, useState } from "react";
import { X, Calendar, Clock, ClipboardList } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTasks, createTaskSubmit } from "../../redux/actions/taskActions";

const DailyTaskPageAttendanceModalPage = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { tasks, loading } = useSelector((state) => state.task);

  const now = new Date();
  const todayDate = now.toISOString().slice(0, 10); // YYYY-MM-DD
  const currentTime = now.toTimeString().slice(0, 8); // HH:MM:SS

  const [form, setForm] = useState({
    task_name: "",
    createTaskDate: todayDate,
    endTaskDate: todayDate,
    start_time: currentTime,
    end_time: currentTime,
    date: todayDate,
    time: currentTime,
    status: "approved",
    location_id: 6,
    latitude: "",
    longitude: "",
    id: "", // for visit purpose
    other_purpose: "", // for custom purpose when "Others" is selected
  });
  console.log(">>>>>>>>>>>>>>", form);

  const [showOtherInput, setShowOtherInput] = useState(false);

  // Get dynamic latitude & longitude
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setForm((prevForm) => ({
            ...prevForm,
            latitude: position.coords.latitude.toString(),
            longitude: position.coords.longitude.toString(),
          }));
        },
        (error) => {
          console.warn("Location error:", error.message);
          alert("Unable to fetch location. Please enable location access.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  }, []);

  useEffect(() => {
    dispatch(fetchTasks());
  }, [dispatch]);

  // Function to validate time
  const validateDateTime = () => {
    const startDate = new Date(form.createTaskDate);
    const endDate = new Date(form.endTaskDate);
    
    // If same day, check if end time is greater than start time
    if (startDate.getTime() === endDate.getTime()) {
      const startTime = form.start_time;
      const endTime = form.end_time;
      
      // Convert time strings to minutes for comparison
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(endTime);
      
      if (endMinutes <= startMinutes) {
        return "End time must be greater than start time for the same day.";
      }
    }
    
    // If end date is before start date
    if (endDate.getTime() < startDate.getTime()) {
      return "End date cannot be before start date.";
    }
    
    return null; // No validation error
  };

  // Helper function to convert time string (HH:MM:SS) to minutes
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "id") {
      // Check if "Others" is selected
      if (value === "Others") {
        setShowOtherInput(true);
      } else {
        setShowOtherInput(false);
        // Clear other_purpose when not selecting "Others"
        setForm((prevForm) => ({
          ...prevForm,
          [name]: value,
          other_purpose: "",
        }));
        return;
      }
    }

    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async () => {
    if (!form.task_name) {
      alert("Please describe the task.");
      return;
    }

    // Validate other_purpose if "Others" is selected
    if (form.id === "Others" && !form.other_purpose.trim()) {
      alert("Please specify the purpose when selecting 'Others'.");
      return;
    }

    // Validate date and time
    const timeValidationError = validateDateTime();
    if (timeValidationError) {
      alert(timeValidationError);
      return;
    }

    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      // Only include other_purpose in API call if "Others" is selected
      if (key === "other_purpose" && form.id !== "Others") {
        return; // Skip adding other_purpose to formData
      }
      formData.append(key, value);
    });

    const res = await dispatch(createTaskSubmit(formData));
    console.log("Shouvik>>>>>>>>", res);
    onSuccess();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-40 p-4">
      {/* Modal container with proper height constraints */}
      <div className="bg-gradient-to-br from-teal-400 to-cyan-500 rounded-2xl w-full max-w-md text-white relative max-h-[90vh] flex flex-col">
        {/* Header - fixed at top */}
        <div className="flex justify-between items-center p-6 pb-4 flex-shrink-0">
          <h2 className="text-xl font-semibold">Add new task</h2>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X size={24} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-sm">
                Describe where are you visiting
              </label>
              <textarea
                name="task_name"
                value={form.task_name}
                onChange={handleChange}
                placeholder="Describe"
                className="w-full p-3 rounded-md text-black resize-none"
                rows={4}
              />
            </div>

            <div>
              <label className="block mb-2 text-sm">Select visit purpose</label>
              <div className="relative text-black">
                <ClipboardList className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" />
                <select
                  className="w-full p-3 pl-10 rounded-md appearance-none"
                  name="id"
                  value={form.id}
                  onChange={handleChange}
                >
                  <option value="">Select purpose</option>
                  {loading && <option>Loading...</option>}
                  {tasks
                    .filter((task) => task.title.toLowerCase() !== "others")
                    .map((task) => (
                      <option key={task.id} value={task.title}>
                        {task.title}
                      </option>
                    ))}
                  <option value="Others">Others</option>
                </select>
              </div>
            </div>

            {/* Conditional input field for "Others" */}
            {showOtherInput && (
              <div>
                <label className="block mb-2 text-sm">Specify other purpose</label>
                <input
                  type="text"
                  name="other_purpose"
                  value={form.other_purpose}
                  onChange={handleChange}
                  placeholder="Enter custom purpose"
                  className="w-full p-3 rounded-md text-black"
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm mb-1">Start Date *</label>
                <div className="relative text-black">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" size={16} />
                  <input
                    type="date"
                    name="createTaskDate"
                    className="w-full pl-10 p-2 rounded-md text-sm"
                    value={form.createTaskDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1">Start Time *</label>
                <div className="relative text-black">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" size={16} />
                  <input
                    type="time"
                    name="start_time"
                    className="w-full pl-10 p-2 rounded-md text-sm"
                    value={form.start_time}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm mb-1">End Date *</label>
                <div className="relative text-black">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" size={16} />
                  <input
                    type="date"
                    name="endTaskDate"
                    className="w-full pl-10 p-2 rounded-md text-sm"
                    value={form.endTaskDate}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1">End Time *</label>
                <div className="relative text-black">
                  <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 z-10" size={16} />
                  <input
                    type="time"
                    name="end_time"
                    className="w-full pl-10 p-2 rounded-md text-sm"
                    value={form.end_time}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - fixed at bottom */}
        <div className="p-6 pt-4 flex-shrink-0">
          <button
            className="bg-yellow-400 text-black font-semibold w-full py-3 rounded-xl hover:bg-yellow-500 transition-colors"
            onClick={handleSubmit}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyTaskPageAttendanceModalPage;